/* eslint-disable sort-imports */
import { useEffect, useRef, useState } from 'react';
import {
  Footer,
  IsLoggedInProvider,
  LinkProvider,
  ThemeProvider,
  Topbar,
} from '@genially/public-web-components';
import { Link } from 'components/Link/Link';
import { OneTap } from 'components/OneTap/OneTap';
import { Seo } from 'components/Seo/Seo';
import {
  get404Content,
  getLangPrefixedPath,
  getUserLang,
  is404Page,
  shouldUpdateLangPath,
} from 'core/infrastructure/404Utils';
import { createLanguageDropdownOptions } from 'core/infrastructure/createLanguageDropdownOptions';
import { parseLangPath } from 'core/infrastructure/parseLangPath';
import { snowplowWrapper } from 'core/services/snowplow';
import { checkPerformanceCookiesEnabled } from 'core/utils/cookies';
import Camara from './assets/camara.png';
import CommunityProfile from './CommunityProfile';
import Europe from './assets/footerx2.png';
import Innoxport from './assets/innoxport.png';
import MegamenuContent from './MegamenuContent';
import SearchContextProvider from '../context/SearchContext';
import configService from 'core/services/configService';
import type { ElementType } from 'react';
import type { PageProps } from 'gatsby';
/* eslint-enable sort-imports */

export type WrapperProps = PageProps<any, { node: { data: any; content404?: any } }>;

declare global {
  interface Window {
    Optanon?: {
      OnConsentChanged: (cb: (e: { detail: string[] }) => void) => void;
    };
    OptanonWrapper?: () => void;
  }
}

const WithLayout = (Component: ElementType): ElementType => {
  const wrapper = ({ pageContext, location }: WrapperProps) => {
    const is404 = is404Page(pageContext?.node?.data?.content);
    const [currentLang, setCurrentLang] = useState(pageContext.node.data.content.langcode.value);
    const [contentToRender, setContentToRender] = useState(pageContext.node.data.content);

    useEffect(() => {
      if (is404 && pageContext.node.content404) {
        const translatedContent = get404Content(pageContext.node.content404);
        setContentToRender(translatedContent);
        setCurrentLang(translatedContent.langcode.value);
      } else {
        setContentToRender(pageContext.node.data.content);
        setCurrentLang(pageContext.node.data.content.langcode.value);
      }
    }, [pageContext.node.content404, pageContext.node.data.content]);

    const { topbar, footer } = configService.data.languages[currentLang];

    topbar.desktop.topbarLinks.map((option: any, index: number) => {
      if (option.megamenu !== null) {
        topbar.desktop.topbarLinks[index].megamenu = option.megamenu;
        topbar.desktop.topbarLinks[index].megamenuRender = (
          <MegamenuContent content={option.megamenu} />
        );
      }
    });

    const content = pageContext.node.data.content;
    let firstCompBg =
      content.paragraphs && content.paragraphs.length > 0
        ? content?.paragraphs[0]?.entity?.behaviorSettings?.rowStyle?.background?.baseColor?.color
          ? content?.paragraphs[0]?.entity?.behaviorSettings?.rowStyle?.background?.baseColor?.color
          : 'rWhite'
        : 'rWhite';

    if (content.bundle === 'template_category') {
      firstCompBg =
        content?.header?.entity?.behaviorSettings?.rowStyle?.background?.baseColor.color;
    }
    const [_scrolling, setScrolling] = useState(firstCompBg);
    const hadPerformanceCookies = useRef(false);

    useEffect(() => {
      hadPerformanceCookies.current = checkPerformanceCookiesEnabled();
      let isSubscribed = true;
      let consentListenerBound = false;

      const handleConsentChange = () => {
        const hasPerformanceCookies = checkPerformanceCookiesEnabled();

        if (!hadPerformanceCookies.current && hasPerformanceCookies) {
          setTimeout(() => {
            snowplowWrapper.sendPageViewEventAfterConsentGrant();
          }, 32);
        }

        hadPerformanceCookies.current = hasPerformanceCookies;
      };

      const bindConsentListener = () => {
        if (!isSubscribed || consentListenerBound || typeof window === 'undefined' || !window.Optanon) {
          return false;
        }

        window.Optanon.OnConsentChanged(() => {
          handleConsentChange();
        });
        consentListenerBound = true;
        return true;
      };

      const previousOptanonWrapper = window.OptanonWrapper;
      window.OptanonWrapper = () => {
        if (typeof previousOptanonWrapper === 'function') {
          previousOptanonWrapper();
        }
        handleConsentChange();
        bindConsentListener();
      };

      if (bindConsentListener()) {
        return () => {
          isSubscribed = false;
        };
      }

      const intervalId = window.setInterval(() => {
        if (bindConsentListener()) {
          window.clearInterval(intervalId);
        }
      }, 250);

      return () => {
        isSubscribed = false;
        window.clearInterval(intervalId);
      };
    }, []);

    function parseFooterLinks(links: any) {
      const parsedLinks = links.map((link: any) => {
        return {
          ...link,
          options: link.options.map((option: any) => ({
            ...option,
            url: parseLangPath(option.url).url.path
              ? parseLangPath(option.url).url.path
              : option.url,
          })),
        };
      });
      return parsedLinks;
    }

    // update url when user lands on 404 landing page
    useEffect(() => {
      if (is404) {
        const userLang = getUserLang();
        const currentPath = location.pathname.startsWith('/')
          ? location.pathname.slice(1)
          : location.pathname;
        if (shouldUpdateLangPath(currentPath, userLang)) {
          window.history.replaceState(null, '', getLangPrefixedPath(currentPath, userLang));
        }
      }
    }, [pageContext, location]);

    useEffect(() => {
      if (is404 && contentToRender?.langcode?.value) {
        setCurrentLang(contentToRender.langcode.value);
      }
    }, [is404, contentToRender]);

    return (
      <ThemeProvider>
        <SearchContextProvider>
          <LinkProvider customLink={Link}>
            <IsLoggedInProvider>
              <Seo content={contentToRender} />
              <Topbar
                url={location.pathname}
                setScrolling={setScrolling}
                firstBg={firstCompBg}
                brandLink={topbar.desktop.linksAndButtons.brandLink}
                links={topbar.desktop.topbarLinks}
                loggedInButtons={topbar.desktop.linksAndButtons.buttonPrimaryLoggedIn}
                buttons={[
                  topbar.desktop.linksAndButtons.buttonStroke,
                  topbar.desktop.linksAndButtons.buttonPrimary,
                ]}
                languageOptions={createLanguageDropdownOptions(
                  topbar.desktop.listLanguageOptions,
                  contentToRender
                )}
              />
              {process.env.NODE_ENV == 'production' && <OneTap />}
              {location?.pathname &&
              (location.pathname.match(/^\/[a-z]{2}\/community\/profile\/?$/) ||
                location.pathname === '/community/profile' ||
                location.pathname === '/community/profile/') ? (
                <CommunityProfile location={location} pageContext={pageContext} />
              ) : (
                <Component content={contentToRender} />
              )}
              <Footer
                brandLink={footer.brandLink}
                lang={contentToRender.langcode.value}
                lists={parseFooterLinks(footer.lists)}
                title={footer.title}
                anonymousButton={footer.anonymousButton}
                loggedInButton={footer.loggedInButton}
                socialMediaLinks={footer.socialMediaLinks}
                languageOptions={createLanguageDropdownOptions(
                  footer.languageOptions,
                  contentToRender
                )}
                europeLogo={Europe}
                innoxportLogo={Innoxport}
                camaraLogo={Camara}
                europeText={footer.europeText}
                europeText2={footer.europeText2}
                europeText3={footer.europeText3}
                europeText4={footer.europeText4}
                europeText5={footer.europeText5}
                europeText6={footer.europeText6}
                europeText7={footer.europeText7}
                bottomLinks={footer.bottomLinks}
                bottomBrandText={footer.bottomBrandText}
              />
            </IsLoggedInProvider>
          </LinkProvider>
        </SearchContextProvider>
      </ThemeProvider>
    );
  };
  return wrapper;
};

export default WithLayout;
